import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, UploadCloud } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { useWarehouseApi } from '@/services/api';
import { t } from '@/i18n';
import type { WzListItem } from '@ip/shared-types';

const ACCEPTED_TYPES = ['application/pdf', 'image/jpeg', 'image/png'];

interface UploadSignedDialogProps {
  nip: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Fired after a successful attach so the list can reload. */
  onUploaded: () => void;
}

/**
 * Signed-scan upload: the backend tries to read the WZ's QR token from the
 * file and route it automatically. When it can't (422) it asks the user to
 * pick the target WZ manually — we then re-submit the SAME file with an
 * explicit wzId. Only `issued` documents are offered (a `returned` one already
 * has its signed scan).
 */
export function UploadSignedDialog({ nip, open, onOpenChange, onUploaded }: UploadSignedDialogProps) {
  const { uploadSigned, listWz } = useWarehouseApi();

  const [file, setFile] = useState<File | null>(null);
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // Manual-selection mode, entered on a 422 (no recognizable QR token).
  const [needsSelection, setNeedsSelection] = useState(false);
  const [options, setOptions] = useState<WzListItem[]>([]);
  const [wzSearch, setWzSearch] = useState('');
  const [selectedWzId, setSelectedWzId] = useState<string | null>(null);

  const reset = useCallback(() => {
    setFile(null);
    setDragging(false);
    setUploading(false);
    setNeedsSelection(false);
    setOptions([]);
    setWzSearch('');
    setSelectedWzId(null);
  }, []);

  useEffect(() => {
    if (!open) reset();
  }, [open, reset]);

  // Refresh the issued-WZ picker whenever the manual step is active and the
  // search term changes.
  useEffect(() => {
    if (!needsSelection) return;
    let active = true;
    listWz(nip, { status: 'issued', search: wzSearch || undefined, pageSize: 50 })
      .then((result) => {
        if (active) setOptions(result.items);
      })
      .catch(() => {
        if (active) setOptions([]);
      });
    return () => {
      active = false;
    };
  }, [needsSelection, nip, wzSearch, listWz]);

  const pickFile = useCallback((candidate: File | null) => {
    if (!candidate) return;
    if (!ACCEPTED_TYPES.includes(candidate.type)) {
      toast.error(t('magazyn.upload.wrongType'));
      return;
    }
    setFile(candidate);
  }, []);

  const handleResult = useCallback(
    (result: { wzNumber: string | null; attachmentCreated: boolean }) => {
      toast.success(t('magazyn.upload.success', { wzNumber: result.wzNumber ?? '' }));
      if (result.attachmentCreated) {
        toast.info(t('magazyn.upload.attachmentCreated'));
      }
      onUploaded();
      onOpenChange(false);
    },
    [onUploaded, onOpenChange],
  );

  const handleSubmit = useCallback(
    async (wzId?: string) => {
      if (!file) return;
      setUploading(true);
      try {
        const result = await uploadSigned(nip, file, wzId);
        if (!result.matched && !wzId) {
          // Defensive: a 2xx that still didn't match falls back to manual pick.
          setNeedsSelection(true);
          return;
        }
        handleResult(result);
      } catch (error) {
        const status = (error as { status?: number })?.status;
        if (status === 422) {
          setNeedsSelection(true);
          return;
        }
        if (status === 409) {
          // The QR was recognized but points at a different document (an
          // acceptance, a plain contractor file, or another company's WZ) — the
          // backend already filed it there. Do NOT offer manual pick.
          toast.error(t('magazyn.upload.foreignToken'));
          return;
        }
        toast.error(t('magazyn.upload.error'));
      } finally {
        setUploading(false);
      }
    },
    [file, nip, uploadSigned, handleResult],
  );

  return (
    <Dialog open={open} onOpenChange={(next) => !uploading && onOpenChange(next)}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t('magazyn.upload.title')}</DialogTitle>
          {needsSelection && (
            <DialogDescription>{t('magazyn.upload.needsSelectionDescription')}</DialogDescription>
          )}
        </DialogHeader>

        <div className="space-y-4">
          <div
            role="button"
            tabIndex={0}
            onClick={() => inputRef.current?.click()}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                inputRef.current?.click();
              }
            }}
            onDragOver={(e) => {
              e.preventDefault();
              setDragging(true);
            }}
            onDragLeave={() => setDragging(false)}
            onDrop={(e) => {
              e.preventDefault();
              setDragging(false);
              pickFile(e.dataTransfer.files?.[0] ?? null);
            }}
            className={cn(
              'flex cursor-pointer flex-col items-center justify-center gap-2 rounded-xl border border-dashed px-4 py-8 text-center transition-colors',
              dragging ? 'border-primary bg-accent' : 'border-input hover:bg-accent/50',
            )}
          >
            <UploadCloud className="text-muted-foreground size-6" />
            <p className="text-sm font-medium">
              {file ? file.name : t('magazyn.upload.dropzoneLabel')}
            </p>
            <p className="text-muted-foreground text-xs">{t('magazyn.upload.dropzoneHint')}</p>
            <input
              ref={inputRef}
              type="file"
              accept="application/pdf,image/jpeg,image/png"
              className="hidden"
              onChange={(e) => pickFile(e.target.files?.[0] ?? null)}
            />
          </div>

          {needsSelection && (
            <div className="space-y-2">
              <p className="text-sm font-medium">{t('magazyn.upload.needsSelectionTitle')}</p>
              <Input
                value={wzSearch}
                onChange={(e) => setWzSearch(e.target.value)}
                placeholder={t('magazyn.upload.searchPlaceholder')}
              />
              <Select value={selectedWzId ?? undefined} onValueChange={setSelectedWzId}>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder={t('magazyn.list.searchPlaceholder')} />
                </SelectTrigger>
                <SelectContent>
                  {options.map((wz) => (
                    <SelectItem key={wz.id} value={wz.id}>
                      {wz.number} — {wz.contractorName}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={uploading}>
            {t('common.cancel')}
          </Button>
          {needsSelection ? (
            <Button
              disabled={uploading || !file || !selectedWzId}
              onClick={() => selectedWzId && handleSubmit(selectedWzId)}
            >
              {uploading && <Loader2 className="size-4 animate-spin" />}
              {t('magazyn.upload.confirm')}
            </Button>
          ) : (
            <Button disabled={uploading || !file} onClick={() => handleSubmit()}>
              {uploading && <Loader2 className="size-4 animate-spin" />}
              {t('magazyn.upload.submit')}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
